import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Loader2 } from "lucide-react";
import Logo from "@/components/Logo";
import SEO from "@/components/SEO";

export default function Register() {
  const navigate = useNavigate();
  const { register } = useAuth();
  const [form, setForm] = useState({ name: "", email: "", password: "", tenant_slug: "" });
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      await register({
        name: form.name,
        email: form.email,
        password: form.password,
        tenant_slug: form.tenant_slug || undefined,
      });
      navigate("/dashboard", { replace: true });
    } catch (e) {
      setErr(e?.response?.data?.detail || "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-white p-8" data-testid="register-page">
      <SEO
        title="Create your workspace — Gr8Boost ERP · 7-day free trial"
        description="Spin up a Gr8Boost ERP workspace in seconds. 7 days full access, no credit card. 8 modules, AI co-pilot, double-entry GL out of the box."
        canonical="https://gr8booster.com/register"
      />
      <form onSubmit={submit} className="w-full max-w-md" data-testid="register-form">
        <Link to="/" className="inline-flex items-center gap-2 mb-10">
          <Logo size={32} />
        </Link>
        <h2 className="font-heading text-3xl font-bold tracking-tighter">Create your account</h2>
        <p className="text-[#495057] mt-2">Join an existing tenant or start fresh in a demo workspace.</p>

        <label className="block mt-8">
          <span className="block text-xs uppercase tracking-[0.15em] text-[#495057] mb-2">Full name</span>
          <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
            data-testid="register-name"
            className="w-full bg-white border border-[#DEE2E6] px-4 py-3 text-sm focus:border-[#0A0A0A] focus:outline-none" />
        </label>
        <label className="block mt-4">
          <span className="block text-xs uppercase tracking-[0.15em] text-[#495057] mb-2">Email</span>
          <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
            data-testid="register-email"
            className="w-full bg-white border border-[#DEE2E6] px-4 py-3 text-sm focus:border-[#0A0A0A] focus:outline-none" />
        </label>
        <label className="block mt-4">
          <span className="block text-xs uppercase tracking-[0.15em] text-[#495057] mb-2">Password (min 6 chars)</span>
          <input required type="password" minLength={6} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
            data-testid="register-password"
            className="w-full bg-white border border-[#DEE2E6] px-4 py-3 text-sm focus:border-[#0A0A0A] focus:outline-none" />
        </label>
        <label className="block mt-4">
          <span className="block text-xs uppercase tracking-[0.15em] text-[#495057] mb-2">Workspace slug <span className="text-[#495057] normal-case tracking-normal">(leave blank for the demo workspace)</span></span>
          <input value={form.tenant_slug} onChange={(e) => setForm({ ...form, tenant_slug: e.target.value })}
            placeholder="e.g. acme-corp"
            data-testid="register-tenant"
            className="w-full bg-white border border-[#DEE2E6] px-4 py-3 text-sm focus:border-[#0A0A0A] focus:outline-none" />
        </label>

        {err && <div data-testid="register-error" className="mt-4 text-sm text-[#D90429] border border-[#D90429] bg-[#D90429]/5 px-4 py-2">{err}</div>}

        <button type="submit" disabled={loading} data-testid="register-submit"
          className="mt-6 w-full inline-flex items-center justify-center gap-2 bg-[#0A0A0A] hover:bg-[#FF4F00] disabled:opacity-60 text-white font-medium px-4 py-3 rounded-sm transition-colors duration-200">
          {loading ? <><Loader2 size={16} className="animate-spin" /> Creating account…</> : "Create account →"}
        </button>
        <div className="mt-6 text-sm text-[#495057]">
          Already have an account? <Link to="/login" className="text-[#0A0A0A] font-medium underline" data-testid="link-login">Sign in</Link>
        </div>
      </form>
    </div>
  );
}
